import { useEffect, useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Card } from '../components/Card'
import { Icon } from '../components/Icon'
import { useAuth } from '../lib/auth'

// Screenshots / page HTML / rejected files the carrier scrapers save when a
// login or download fails (server: logs/debug/<carrier>/). Owner + admin only.
// HTML files are download-only — they are copies of carrier web pages with the
// carriers' own scripts and must never be opened inside this dashboard.

interface DebugFile {
  carrier: string
  name: string
  size: number
  modified: string
  kind: 'image' | 'html' | 'text' | 'rejected'
}
interface ListResp { root_exists: boolean; retention_days: number; files: DebugFile[]; pruned: number }

const fileUrl = (f: DebugFile) =>
  `/api/debug-files/${encodeURIComponent(f.carrier)}/${encodeURIComponent(f.name)}`

function fmtSize(n: number): string {
  return n < 1024 ? `${n} B` : n < 1048576 ? `${(n / 1024).toFixed(1)} KB` : `${(n / 1048576).toFixed(1)} MB`
}
function fmtWhen(iso: string): string {
  return new Date(iso).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' })
}
const KIND_LABEL: Record<DebugFile['kind'], string> = {
  image: 'Screenshot', html: 'Page HTML', text: 'Text / log', rejected: 'Rejected file',
}

export function DebugFilesView() {
  const { isAdmin } = useAuth()
  const qc = useQueryClient()
  const [carrier, setCarrier] = useState<string>('all')
  const [preview, setPreview] = useState<{ file: DebugFile; url: string } | null>(null)
  const [confirming, setConfirming] = useState<string | null>(null)   // key of the file awaiting confirm
  const [busy, setBusy] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  const { data, isLoading } = useQuery<ListResp>({
    queryKey: ['debug-files'],
    queryFn: async () => {
      const r = await fetch('/api/debug-files')
      if (!r.ok) throw new Error((await r.json().catch(() => null))?.error || `HTTP ${r.status}`)
      return r.json()
    },
    enabled: isAdmin,
  })

  // free the preview image when the modal closes
  useEffect(() => () => { if (preview) URL.revokeObjectURL(preview.url) }, [preview])
  useEffect(() => {
    if (!preview) return
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setPreview(null) }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [preview])

  if (!isAdmin) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] text-center px-4">
        <Icon name="eye-off" className="w-7 h-7 text-ink-400 mb-4" />
        <h2 className="text-[18px] font-bold text-ink-900 mb-2">Owner and admin only</h2>
        <p className="text-sm text-ink-500 max-w-sm">Debug files can contain account and invoice details.</p>
      </div>
    )
  }

  const key = (f: DebugFile) => `${f.carrier}/${f.name}`
  const carriers = [...new Set((data?.files ?? []).map(f => f.carrier))].sort()
  const files = (data?.files ?? []).filter(f => carrier === 'all' || f.carrier === carrier)

  async function fetchBlob(f: DebugFile): Promise<Blob> {
    const r = await fetch(fileUrl(f))
    if (!r.ok) throw new Error((await r.json().catch(() => null))?.error || `HTTP ${r.status}`)
    return r.blob()
  }
  async function openPreview(f: DebugFile) {
    setError(null); setBusy(key(f))
    try {
      setPreview({ file: f, url: URL.createObjectURL(await fetchBlob(f)) })
    } catch (e) { setError((e as Error).message) } finally { setBusy(null) }
  }
  async function download(f: DebugFile) {
    setError(null); setBusy(key(f))
    try {
      const url = URL.createObjectURL(await fetchBlob(f))
      const a = document.createElement('a'); a.href = url; a.download = f.name; a.click()
      setTimeout(() => URL.revokeObjectURL(url), 10_000)
    } catch (e) { setError((e as Error).message) } finally { setBusy(null) }
  }
  async function remove(f: DebugFile) {
    setError(null); setBusy(key(f))
    try {
      const r = await fetch(fileUrl(f), { method: 'DELETE' })
      if (!r.ok) throw new Error((await r.json().catch(() => null))?.error || `HTTP ${r.status}`)
      setConfirming(null)
      await qc.invalidateQueries({ queryKey: ['debug-files'] })
    } catch (e) { setError((e as Error).message) } finally { setBusy(null) }
  }

  return (
    <div>
      <div className="mb-5">
        <div className="text-[12px] font-semibold text-flame-600 uppercase tracking-wider">System</div>
        <h1 className="text-[28px] font-bold tracking-tight mt-1">Debug files</h1>
        <p className="text-ink-500 text-[13px] mt-1">
          Screenshots and page copies saved by the carrier scrapers when a login or download fails.
          Files older than {data?.retention_days ?? 14} days are deleted automatically. Page HTML is download-only.
        </p>
      </div>

      {error && <div className="mb-3 px-4 py-2 rounded-lg bg-flame-50 border border-flame-200 text-flame-700 text-[13px]">{error}</div>}

      <Card className="overflow-hidden">
        <div className="px-5 py-3 flex items-center justify-between border-b border-ink-100">
          <div className="flex items-center bg-ink-100 rounded-lg p-0.5 text-[12.5px] font-medium">
            {['all', ...carriers].map(c => (
              <button key={c} onClick={() => setCarrier(c)}
                className={`px-3 py-1 rounded-md ${carrier === c ? 'bg-white shadow-sm' : 'text-ink-500'}`}>
                {c === 'all' ? 'All' : c.toUpperCase()}
              </button>
            ))}
          </div>
          <button onClick={() => qc.invalidateQueries({ queryKey: ['debug-files'] })}
            className="h-8 px-3 rounded-lg border border-ink-200 bg-white hover:bg-ink-50 font-semibold text-[12.5px] inline-flex items-center gap-1.5">
            <Icon name="refresh-cw" className="w-3.5 h-3.5" /> Refresh
          </button>
        </div>

        {isLoading ? (
          <div className="p-8 text-center text-ink-400 text-[13px]">Loading…</div>
        ) : data && !data.root_exists ? (
          <div className="p-8 text-center text-ink-500 text-[13px]">The debug folder is not mounted on this server yet (logs/debug).</div>
        ) : files.length === 0 ? (
          <div className="p-8 text-center text-ink-500 text-[13px]">No debug files — nothing has failed recently.</div>
        ) : (
          <table className="w-full text-[13px]">
            <thead className="bg-ink-50 text-ink-500 text-[10.5px] uppercase tracking-wider">
              <tr>
                <th className="text-left font-semibold py-2 px-5">Saved</th>
                <th className="text-left font-semibold py-2">Carrier</th>
                <th className="text-left font-semibold py-2">File</th>
                <th className="text-left font-semibold py-2">Type</th>
                <th className="text-right font-semibold py-2">Size</th>
                <th className="text-right font-semibold py-2 px-5">Actions</th>
              </tr>
            </thead>
            <tbody>
              {files.map(f => {
                const k = key(f)
                return (
                  <tr key={k} className="border-t border-ink-100">
                    <td className="py-2 px-5 whitespace-nowrap text-ink-600">{fmtWhen(f.modified)}</td>
                    <td className="py-2 font-semibold">{f.carrier.toUpperCase()}</td>
                    <td className="py-2 mono text-[12px] break-all pr-3">{f.name}</td>
                    <td className="py-2 text-ink-600">{KIND_LABEL[f.kind]}</td>
                    <td className="py-2 text-right text-ink-600 whitespace-nowrap">{fmtSize(f.size)}</td>
                    <td className="py-2 px-5 text-right whitespace-nowrap">
                      {confirming === k ? (
                        <span className="inline-flex items-center gap-1.5">
                          <span className="text-[12px] text-flame-700 font-semibold">Delete this file?</span>
                          <button onClick={() => remove(f)} disabled={busy === k}
                            className="h-7 px-2.5 rounded-md bg-flame-500 hover:bg-flame-600 text-white text-[12px] font-semibold disabled:opacity-60">
                            {busy === k ? 'Deleting…' : 'Confirm delete'}
                          </button>
                          <button onClick={() => setConfirming(null)}
                            className="h-7 px-2.5 rounded-md border border-ink-200 text-[12px]">Cancel</button>
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1">
                          {f.kind === 'image' && (
                            <button onClick={() => openPreview(f)} disabled={busy === k} title="Preview"
                              className="h-7 w-7 grid place-items-center rounded-md hover:bg-ink-100 disabled:opacity-60">
                              <Icon name="eye" className="w-4 h-4" />
                            </button>
                          )}
                          <button onClick={() => download(f)} disabled={busy === k} title="Download"
                            className="h-7 w-7 grid place-items-center rounded-md hover:bg-ink-100 disabled:opacity-60">
                            <Icon name="download" className="w-4 h-4" />
                          </button>
                          <button onClick={() => setConfirming(k)} title="Delete"
                            className="h-7 w-7 grid place-items-center rounded-md hover:bg-flame-50 text-flame-600">
                            <Icon name="trash-2" className="w-4 h-4" />
                          </button>
                        </span>
                      )}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        )}
      </Card>

      {preview && (
        <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-6" onClick={() => setPreview(null)}>
          <div className="bg-white rounded-xl max-w-[95vw] max-h-[92vh] overflow-auto p-3" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-2 gap-4">
              <div className="mono text-[12px] text-ink-600 break-all">{preview.file.carrier.toUpperCase()} · {preview.file.name}</div>
              <button onClick={() => setPreview(null)} className="h-7 w-7 grid place-items-center rounded-md hover:bg-ink-100">
                <Icon name="x" className="w-4 h-4" />
              </button>
            </div>
            <img src={preview.url} alt={preview.file.name} className="max-w-full" />
          </div>
        </div>
      )}
    </div>
  )
}
